import * as React from 'react'

import { ConnectError } from '@connectrpc/connect'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'

import { useAuth, withAuthenticationRequired } from '@enechain/auth-js'
import {
  Box,
  Flex,
  Loading,
  LoadingEnechainLogo,
  PageError,
  Stack,
  TertiaryButton,
  Text,
  TextButton,
} from '@enechain/ecloud-designsystem'
import {
  ECloudSymbol,
  FaArrowRotateRightRegular,
  FaArrowUpRightFromSquareRegular,
  FaTriangleExclamationRegular,
} from '@enechain/ecloud-icons'

import { useGetMe } from './useGetMe'

type Props = {
  eCloudUrl: string
  children: React.ReactNode
}

// memo(shunya): Design System 側の I/F の都合、修正次第削除
const DEFAULT_PAGE_ERROR_PROPERTIES = {
  Icon: FaTriangleExclamationRegular,
  title: '',
  description: '',
  buttonText: '',
}

const _ProtectedTermAgreeGuard: React.FC<Props> = ({ eCloudUrl, children }) => {
  const { t } = useTranslation(['common', 'component'])

  const { isAdmin, isAgreed, isLoading } = useGetMe()

  const params = new URLSearchParams(window.location.search)
  const skipTermCheck = params.get('skip-term-check') === '1'

  if (isLoading) {
    return (
      <Flex height="100vh" justifyContent="center" alignItems="center">
        <Loading />
      </Flex>
    )
  }

  if (!isAgreed && !skipTermCheck) {
    return (
      <Flex
        height="100vh"
        justifyContent="center"
        alignItems="center"
        padding="6"
      >
        <Stack gap="8">
          <Stack gap="2">
            <Text textStyle="headerMd" textAlign="center">
              {t('component:layouts.protected_term_agree_guard.title')}
            </Text>
            <Text textStyle="textMd" textAlign="center">
              {t(
                'component:layouts.protected_term_agree_guard.description_line_1',
              )}
              <br />
              {isAdmin ? (
                t(
                  'component:layouts.protected_term_agree_guard.description_line_2_for_admin',
                )
              ) : (
                <>
                  {t(
                    'component:layouts.protected_term_agree_guard.description_line_2_for_member',
                  )}
                  <br />
                  {t(
                    'component:layouts.protected_term_agree_guard.description_line_3_for_member',
                  )}
                </>
              )}
            </Text>
          </Stack>
          <Stack justifyContent="center" gap="4">
            {isAdmin ? (
              <Box textAlign="center">
                <TertiaryButton
                  borderColor="Border.accentPrimary"
                  color="Object.accentPrimary"
                  leftIcon={<ECloudSymbol boxSize="20px" />}
                  rightIcon={
                    <FaArrowUpRightFromSquareRegular
                      boxSize="24px"
                      color="Object.accentPrimary"
                    />
                  }
                  onClick={() => {
                    window.open(eCloudUrl, 'mozillaWindow')
                  }}
                >
                  {t(
                    'component:layouts.protected_term_agree_guard.link_to_ecloud_for_admin',
                  )}
                </TertiaryButton>
              </Box>
            ) : (
              <Box textAlign="center">
                <TertiaryButton
                  leftIcon={<ECloudSymbol boxSize="20px" />}
                  rightIcon={<FaArrowUpRightFromSquareRegular boxSize="24px" />}
                  onClick={() => {
                    window.open(eCloudUrl, 'mozillaWindow')
                  }}
                >
                  {t(
                    'component:layouts.protected_term_agree_guard.link_to_ecloud_for_member',
                  )}
                </TertiaryButton>
              </Box>
            )}
            <Box textAlign="center">
              <TextButton
                leftIcon={<FaArrowRotateRightRegular boxSize="20px" />}
                onClick={() => {
                  window.location.reload()
                }}
              >
                {t('component:layouts.protected_term_agree_guard.reload')}
              </TextButton>
            </Box>
          </Stack>
        </Stack>
      </Flex>
    )
  }

  return children
}

export const ProtectedTermAgreeGuard: React.FC<Props> =
  withAuthenticationRequired(
    ({ eCloudUrl, children }) => {
      const { t } = useTranslation(['component'])
      const { logout } = useAuth()

      return (
        <ErrorBoundary
          FallbackComponent={({ ...rest }): React.ReactElement => {
            const error = rest.error as ConnectError
            return (
              <PageError
                kind="500"
                defaultErrorProperties={{
                  '401': DEFAULT_PAGE_ERROR_PROPERTIES,
                  '403': DEFAULT_PAGE_ERROR_PROPERTIES,
                  '404': DEFAULT_PAGE_ERROR_PROPERTIES,
                  '500': {
                    Icon: FaTriangleExclamationRegular,
                    title: t('component:layouts.main_layout.not_access'),
                    description: `${t('component:layouts.main_layout.not_access_description')}\n${error.message}`,
                    buttonText: t('component:layouts.main_layout.sign_out'),
                  },
                  '501': DEFAULT_PAGE_ERROR_PROPERTIES,
                }}
                onNavigate={(): void => logout()}
              />
            )
          }}
        >
          <_ProtectedTermAgreeGuard eCloudUrl={eCloudUrl}>
            {children}
          </_ProtectedTermAgreeGuard>
        </ErrorBoundary>
      )
    },
    {
      onLoading: () => <LoadingEnechainLogo />,
      onRedirecting: () => <LoadingEnechainLogo />,
      returnTo: window.location.href,
    },
  )
